import { useState, useEffect, useRef, useCallback } from 'react';

interface AnimationConfig {
  duration?: number; // Animation duration in milliseconds
  fields?: string[]; // Fields to monitor for changes
}

interface FirebaseAnimationResult<T> {
  animatedItems: Set<T>;
  shouldAnimateItem: (item: T) => boolean;
}

/**
 * Custom hook to handle Firebase data change animations
 * @param firebaseData - The Firebase data to monitor
 * @param config - Configuration for animation behavior
 * @returns Object containing animation state and helper functions
 */
export const useFirebaseAnimation = <T extends string>(
  firebaseData: any,
  config: AnimationConfig = {},
): FirebaseAnimationResult<T> => {
  const { duration = 2000, fields = [] } = config;

  const [animatedItems, setAnimatedItems] = useState<Set<T>>(new Set());
  const previousFirebaseDataRef = useRef<any>(null);

  useEffect(() => {
    if (firebaseData && previousFirebaseDataRef.current) {
      const currentData = firebaseData;
      const previousData = previousFirebaseDataRef.current;

      const itemsToAnimate = new Set<T>();

      // Generic comparison logic for different data structures

      // Check ESP-to-ESP data first (most specific)
      if (currentData.espToEsp && previousData.espToEsp) {
        // For ESP-to-ESP data (like esp-placement-report)
        Object.keys(currentData.espToEsp).forEach((espKey) => {
          const current = currentData.espToEsp[espKey];
          const previous = previousData.espToEsp[espKey];

          if (previous) {
            fields.forEach((field) => {
              if (current[field] !== previous[field]) {
                itemsToAnimate.add(`${espKey}-${field}` as T);
              }
            });
          }
        });
      }

      // Check summary data (for inbox-placement-score-card and test-report-content)
      if (currentData.summary && previousData.summary) {
        fields.forEach((field) => {
          if (currentData.summary[field] !== previousData.summary[field]) {
            itemsToAnimate.add(field as T);
          }
        });

        // Special case for sentCount (test-report-content)
        if (fields.includes('placementCount')) {
          const currentSentCount = currentData.summary?.placementCount;
          const previousSentCount = previousData.summary?.placementCount;
          if (currentSentCount !== previousSentCount) {
            itemsToAnimate.add('sentCount' as T);
          }
        }
      }

      // Check account-level data (for overview, sender-email-accounts)
      if (
        typeof currentData === 'object' &&
        !Array.isArray(currentData) &&
        !currentData.summary &&
        !currentData.espToEsp
      ) {
        Object.keys(currentData).forEach((accountId) => {
          const currentAccount = currentData[accountId];
          const previousAccount = previousData[accountId];

          if (previousAccount) {
            // Check placement summary changes
            if (
              currentAccount.placementSummary &&
              previousAccount.placementSummary
            ) {
              fields.forEach((field) => {
                const currentCount =
                  currentAccount.placementSummary[field]?.total;
                const previousCount =
                  previousAccount.placementSummary[field]?.total;
                if (currentCount !== previousCount) {
                  itemsToAnimate.add(`${accountId}-${field}` as T);
                }
              });
            }

            // Check ESP-wise changes for sender-email-accounts
            if (
              currentAccount.seedAccountEspWise &&
              previousAccount.seedAccountEspWise
            ) {
              Object.keys(currentAccount.seedAccountEspWise).forEach(
                (espType) => {
                  const currentEspData =
                    currentAccount.seedAccountEspWise[espType];
                  const previousEspData =
                    previousAccount.seedAccountEspWise[espType];

                  if (
                    currentEspData?.placementSummary &&
                    previousEspData?.placementSummary
                  ) {
                    fields.forEach((field) => {
                      const currentCount =
                        currentEspData.placementSummary[field];
                      const previousCount =
                        previousEspData.placementSummary[field];
                      if (currentCount !== previousCount) {
                        itemsToAnimate.add(
                          `${accountId}-${espType}-${field}` as T,
                        );
                      }
                    });
                  }
                },
              );
            }

            // Check spam score changes
            const currentSpamScore = currentAccount.spamAnalysis?.score;
            const previousSpamScore = previousAccount.spamAnalysis?.score;
            if (currentSpamScore !== previousSpamScore) {
              itemsToAnimate.add(`${accountId}-spamScore` as T);
            }
          }
        });
      }

      if (itemsToAnimate.size > 0) {
        setAnimatedItems(itemsToAnimate);

        // Remove animation class after animation completes
        setTimeout(() => {
          setAnimatedItems(new Set());
        }, duration);
      }
    }

    // Update previous data reference
    previousFirebaseDataRef.current = firebaseData;
  }, [firebaseData, duration, fields]);

  const shouldAnimateItem = useCallback(
    (item: T): boolean => animatedItems.has(item),
    [animatedItems],
  );

  return {
    animatedItems,
    shouldAnimateItem,
  };
};
